'use client';

/**
 * MinimalToolCard - Unified card component for displaying tool calls
 *
 * This is the primary component for rendering tool call states in the chat UI.
 * It supports:
 * - Multiple states: started, completed, error
 * - HITL (Human-in-the-Loop) approval workflow: pending, approved, rejected
 * - Collapsible content area for detailed output
 * - Metadata display (status codes, methods, etc.)
 * - Copy and expand actions
 *
 * Usage:
 * - For action tools (HTTP, Terminal, Python, Browser): Use this card for both
 *   HITL approval and result display
 * - For intelligence tools: Use InlineToolExpand instead
 */
import type { ReactNode, MouseEvent } from 'react';
import { useState } from 'react';
import { HStack, Box } from '@terra/ui-styled-system';
import { Text } from '../../../components/styled/text';
import {
  Tick01Icon,
  CheckmarkCircle02Icon,
  Copy01Icon,
  Edit03Icon,
  CancelCircleIcon,
} from '@terra/ui-icons';
import { Collapsible } from '../../../components/collapsible';
import { IconButton } from '../../../components/icon-button';
import { Tooltip } from '../../../components/tooltip';
import type { HITLStatus, HITLActionHandlers } from './hitl-types';
import { HITLStatusIndicator } from './HITLStatusIndicator';
import { TraceLine } from './trace';
import {
  CHAT_STEP_LABEL_SIZE,
  CHAT_STEP_LABEL_COLOR,
} from './chat-nesting-layout';
import { useChatExpansionState } from '../expansion/chat-expansion-state';

// =============================================================================
// Types
// =============================================================================
type ToolStatus = 'started' | 'completed' | 'error';
type MetadataItem = {
  label?: string;
  value: string | number;
  icon?: ReactNode;
  color?: 'green' | 'red' | 'orange' | 'blue';
};
type CustomAction = {
  icon: ReactNode;
  label: string;
  onClick: () => void;
};
type TimingInfo = {
  durationMs: number;
};
type MinimalToolCardProps = {
  /** Icon to display next to the tool name */
  icon?: ReactNode;
  /** Display name of the tool */
  toolName: string;
  /** Current execution status */
  status: ToolStatus | string;
  /** Metadata items to display (e.g., HTTP status, method) */
  metadata?: MetadataItem[];
  /** Error message if status is 'error' */
  error?: string;
  /** Execution timing information */
  timing?: TimingInfo;
  /** Content to display in the collapsible area */
  children?: ReactNode;
  /** Callback when copy button is clicked */
  onCopy?: () => void;
  onExpand?: () => void;
  /** Whether the card body is open (chevron) */
  defaultExpanded?: boolean;
  forceExpanded?: boolean;
  autoCollapseWhenComplete?: boolean;
  expansionKey?: string;
  /** Custom action buttons for the header */
  customActions?: CustomAction[];
  /** Additional header actions (ReactNode) */
  headerActions?: ReactNode;
  /** HITL approval status */
  hitlStatus?: HITLStatus;
} & HITLActionHandlers;

// =============================================================================
// Helpers
// =============================================================================
function formatDuration(durationMs: number): string {
  if (durationMs < 1000) {
    return `${Math.round(durationMs)}ms`;
  }

  return `${(durationMs / 1000).toFixed(2)}s`;
}

// =============================================================================
// Sub-components
// =============================================================================
type HITLActionsProps = {
  hitlStatus: HITLStatus;
  onApprove?: () => void;
  onReject?: () => void;
  onEdit?: () => void;
  disabled?: boolean;
};

function HITLActions({
  hitlStatus,
  onApprove,
  onReject,
  onEdit,
  disabled,
}: HITLActionsProps) {
  const stopPropagation = (e: MouseEvent) => e.stopPropagation();

  if (hitlStatus === 'pending') {
    if (!onReject && !onEdit && !onApprove) return null;

    return (
      <HStack gap={0.5} onClick={stopPropagation}>
        {onReject && (
          <Tooltip title="Reject" openDelay={300}>
            <IconButton
              variant="ghost"
              size="xs"
              aria-label="Reject"
              onClick={onReject}
              disabled={disabled}
              color="fg.error"
              _hover={{ color: 'fg.error' }}
            >
              <CancelCircleIcon size={14} />
            </IconButton>
          </Tooltip>
        )}
        {onApprove && (
          <Tooltip title="Approve" openDelay={300}>
            <IconButton
              variant="ghost"
              size="xs"
              aria-label="Approve"
              onClick={onApprove}
              disabled={disabled}
              color="fg.neutral.success"
              _hover={{ color: 'fg.neutral.success' }}
            >
              <CheckmarkCircle02Icon size={14} />
            </IconButton>
          </Tooltip>
        )}
        {onEdit && (
          <Tooltip title="Edit" openDelay={300}>
            <IconButton
              variant="ghost"
              size="xs"
              aria-label="Edit"
              onClick={onEdit}
              disabled={disabled}
              color="fg.muted"
              _hover={{ color: 'fg.default' }}
            >
              <Edit03Icon size={14} />
            </IconButton>
          </Tooltip>
        )}
      </HStack>
    );
  }

  return <HITLStatusIndicator status={hitlStatus} />;
}

type CardHeaderProps = {
  toolName: string;
  metadata?: MetadataItem[];
  durationText?: string | null;
  isError: boolean;
  isCompleted: boolean;
  hitlStatus?: HITLStatus;
  hitlActions?: HITLActionHandlers;
  customActions: CustomAction[];
  headerActions?: ReactNode;
  onCopy?: () => void;
  hasContent: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
  copied: boolean;
};

function CardHeader({
  toolName,
  metadata,
  durationText,
  isError,
  isCompleted,
  hitlStatus,
  hitlActions,
  customActions,
  headerActions,
  onCopy,
  hasContent,
  isExpanded,
  onToggleExpand,
  copied,
}: CardHeaderProps) {
  const stopPropagation = (e: MouseEvent) => e.stopPropagation();
  const isRejected = hitlStatus === 'reject';
  const showCollapsedCompletedState =
    isCompleted && !isExpanded && !isError && !isRejected;
  const hasPinnedMetadata = metadata?.some((item) => item.label === 'User');
  const showHeaderDetails = !showCollapsedCompletedState || hasPinnedMetadata;

  return (
    <Box onClick={hasContent ? onToggleExpand : undefined}>
      <TraceLine
        title={toolName}
        canExpand={hasContent}
        isExpanded={isExpanded}
        status={isError ? 'error' : undefined}
        meta={
        <>
        {showHeaderDetails && (
          <HStack
            alignItems="center"
            gap={1}
            flex={1}
            minWidth={0}
            overflow="hidden"
          >
            {metadata?.map((item, index) => {
              const itemColor = isError
                ? 'fg.error'
                : ({
                    _light: 'neutral.light.9',
                    _dark: 'neutral.dark.8',
                  } as const);
              const shouldTruncate = item.label === 'User';

              return (
                <HStack
                  key={index}
                  alignItems="center"
                  gap={1}
                  minWidth={0}
                  flexShrink={shouldTruncate ? 1 : 0}
                >
                  <Text
                    fontSize={CHAT_STEP_LABEL_SIZE}
                    color={CHAT_STEP_LABEL_COLOR}
                    flexShrink={0}
                  >
                    ·
                  </Text>
                  {item.icon && (
                    <Box
                      color={CHAT_STEP_LABEL_COLOR}
                      display="flex"
                      alignItems="center"
                      flexShrink={0}
                    >
                      {item.icon}
                    </Box>
                  )}
                  {item.label && item.label !== 'Status' && (
                    <Text
                      fontSize={CHAT_STEP_LABEL_SIZE}
                      color={CHAT_STEP_LABEL_COLOR}
                      flexShrink={0}
                    >
                      {item.label}:
                    </Text>
                  )}
                  <Text
                    fontSize={CHAT_STEP_LABEL_SIZE}
                    color={itemColor}
                    truncate={shouldTruncate ? true : undefined}
                    flexShrink={shouldTruncate ? 1 : 0}
                  >
                    {item.value}
                  </Text>
                </HStack>
              );
            })}

            {durationText && isCompleted && (
              <HStack gap={1} alignItems="center" flexShrink={0}>
                <Text
                  fontSize={CHAT_STEP_LABEL_SIZE}
                  color={{ _light: 'neutral.light.9', _dark: 'neutral.dark.8' }}
                >
                  ·
                </Text>
                <Text
                  fontSize={CHAT_STEP_LABEL_SIZE}
                  color={{ _light: 'neutral.light.9', _dark: 'neutral.dark.8' }}
                >
                  {durationText}
                </Text>
              </HStack>
            )}
          </HStack>
        )}

        {!showHeaderDetails && <Box flex={1} />}
        </>
        }
        trailing={
          <>
        {hitlStatus && (
          <HITLActions
            hitlStatus={hitlStatus}
            onApprove={hitlActions?.onApprove}
            onReject={hitlActions?.onReject}
            onEdit={hitlActions?.onEdit}
            disabled={hitlActions?.disabled}
          />
        )}

        <HStack gap={0.5} flexShrink={0}>
          {customActions.map((action, index) => (
            <Tooltip
              key={index}
              openDelay={500}
              closeDelay={0}
              title={action.label}
            >
              <IconButton
                variant="ghost"
                size="xs"
                aria-label={action.label}
                onClick={(event) => {
                  event.stopPropagation();
                  action.onClick();
                }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
          {headerActions && customActions.length > 0 && (
            <Box
              height="14px"
              width="1px"
              borderLeftWidth="1px"
              borderLeftColor="border.default"
              mx={1}
              flexShrink={0}
            />
          )}
          {headerActions}
          {onCopy && (
            <Tooltip
              openDelay={500}
              closeDelay={0}
              title={copied ? 'Copied' : 'Copy'}
            >
              <IconButton
                variant="ghost"
                size="xs"
                aria-label="Copy"
                onClick={(event) => {
                  stopPropagation(event);
                  onCopy();
                }}
              >
                {copied ? <Tick01Icon size={11} /> : <Copy01Icon size={11} />}
              </IconButton>
            </Tooltip>
          )}
        </HStack>
          </>
        }
      />
    </Box>
  );
}

// =============================================================================
// Main Component
// =============================================================================
export function MinimalToolCard({
  toolName,
  status,
  metadata,
  error,
  timing,
  children,
  onCopy,
  defaultExpanded = false,
  forceExpanded = false,
  customActions = [],
  headerActions,
  hitlStatus,
  onApprove,
  onReject,
  onEdit,
  disabled,
  expansionKey,
}: MinimalToolCardProps) {
  const isError = status === 'error' || !!error;
  const isCompleted = status === 'completed';
  const isForceExpanded = forceExpanded;
  const [isExpanded, setIsExpanded] = useChatExpansionState({
    expansionKey,
    defaultExpanded: defaultExpanded || isError || forceExpanded,
    forceExpanded,
  });
  const effectiveIsExpanded = isForceExpanded || isExpanded;
  const [copied, setCopied] = useState(false);
  const durationText = timing ? formatDuration(timing.durationMs) : null;
  const hasContent = !!(children || error);

  const handleCopy = () => {
    if (onCopy) {
      onCopy();
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Box
      bg="transparent"
      borderWidth="0"
      borderRadius="md"
      width="100%"
      display="flex"
      flexDirection="column"
    >
      <Collapsible.Root
        open={effectiveIsExpanded}
        onOpenChange={({ open }) => {
          if (isForceExpanded) {
            return;
          }

          setIsExpanded(open);
        }}
      >
        <CardHeader
          toolName={toolName}
          metadata={metadata}
          durationText={durationText}
          isError={isError}
          isCompleted={isCompleted}
          hitlStatus={hitlStatus}
          hitlActions={{ onApprove, onReject, onEdit, disabled }}
          customActions={customActions}
          headerActions={headerActions}
          onCopy={onCopy ? handleCopy : undefined}
          hasContent={hasContent}
          isExpanded={effectiveIsExpanded}
          onToggleExpand={() => {
            if (!isForceExpanded) {
              setIsExpanded(!isExpanded);
            }
          }}
          copied={copied}
        />

        {hasContent && (
          <Collapsible.Content
            style={{ transition: 'max-height 200ms ease-out' }}
          >
            <Box pr={2} py={0.5} mt={1}>
              {error && !children && (
                <Box borderRadius="sm" p={2}>
                  <Text fontSize="sm" color="fg.error">
                    {error}
                  </Text>
                </Box>
              )}
              {children}
            </Box>
          </Collapsible.Content>
        )}
      </Collapsible.Root>
    </Box>
  );
}

// Re-export types for consumers
export type { MinimalToolCardProps, MetadataItem, CustomAction, TimingInfo };
